import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { createReactRenderer } from 'helpers/react-renderer';
import { showErrors } from 'pages/utils/errors';
import { sanitizeText } from 'pages/utils/sanitize';

import Modal, { ModalHeader, ModalBody } from '@lightspeed/cirrus-modal';
import { CardSection } from '@lightspeed/cirrus-card';
import Text from '@lightspeed/cirrus-text';
import Checkbox from '@lightspeed/cirrus-checkbox';
import { Button } from '@lightspeed/flame/Button';

const textContent = {
  employee: {
    openModalButton: 'Delete Account',
    deleteModal: {
      headerTitle: 'You are about to delete this account',
      imageTitle: 'Delete account',
      checklistTitle: 'Deleting this account will:',
      checklistOptionOne: '<strong>Remove</strong> all links where this account is attached.',
      checklistOptionTwo: '<strong>Remove</strong> this profile page.',
      checklistOptionThree:
        '<strong>Keep</strong> all activity done by this account with an anonymized identifier.',
      warning: 'This action cannot be undone.',
      confirmation: 'Are you sure you want to delete this account?',
      agreement: (firstName, lastName) =>
        `Yes, I want to delete <var>${firstName}${lastName && ` ${lastName}`}</var>’s account.`,
      buttonAgree: 'Delete this account',
    },
    successModal: {
      headerTitle: 'Account deleted',
      returnToListButton: 'Return to employee setup',
    },
  },
  customer: {
    openModalButton: 'Delete Customer',
    deleteModal: {
      headerTitle: 'You are about to delete this customer',
      imageTitle: 'Delete account',
      checklistTitle: 'Deleting this customer profile will:',
      checklistOptionOne: '<strong>Remove</strong> all links where this customer is attached.',
      checklistOptionTwo: '<strong>Remove</strong> this profile page.',
      checklistOptionThree:
        '<strong>Keep</strong> all activity linked to this customer with an anonymized identifier.',
      warning: 'This action cannot be undone.',
      confirmation: 'Are you sure you want to delete this customer profile?',
      agreement: (firstName, lastName) =>
        `Yes, I want to delete <var>${firstName}${lastName &&
          ` ${lastName}`}</var>’s customer profile.`,
      buttonAgree: 'Delete this customer',
    },
    successModal: {
      headerTitle: 'Customer profile deleted',
      returnToListButton: 'Return to customer list',
    },
  },
};

const config = {
  employee: {
    deleteUrl: id => `/admin/employees/${id}`,
    listUrl: '/?name=admin.listings.employees&form_name=listing',
  },
  customer: {
    deleteUrl: id => `/admin/customers/${id}`,
    listUrl: '/?name=customer.listings.customers&form_name=listing',
  },
};

class DeleteAccount extends Component {
  constructor(props) {
    super(props);

    this.state = {
      renderModals: false,
      isOpen: false,
      isAgreed: false,
      isDeleted: false,
      isLoading: false,
    };
  }

  handleOpenModal() {
    this.setState({
      renderModals: true,
      isOpen: true,
    });
  }

  handleCloseModal() {
    const { isLoading } = this.state;

    if (!isLoading) {
      this.setState({
        renderModals: false,
        isOpen: false,
        isAgreed: false,
        isDeleted: false,
      });
    }
  }

  handleCheckAgree() {
    this.setState(prevState => ({
      isAgreed: !prevState.isAgreed,
    }));
  }

  async handleClickDelete() {
    const { id, type } = this.props;
    try {
      this.setState({
        isLoading: true,
      });
      await axios.delete(config[type].deleteUrl(id));
      this.setState({
        isOpen: false,
        isDeleted: true,
        isLoading: false,
      });
    } catch (e) {
      this.setState(
        {
          isLoading: false,
        },
        () => {
          this.handleCloseModal();
          showErrors(e);
        }
      );
    }
  }

  handleReturnToParent() {
    const { type } = this.props;

    this.setState(
      {
        renderModals: false,
        isOpen: false,
        isAgreed: false,
        isDeleted: false,
      },
      () => {
        window.location.replace(config[type].listUrl);
      }
    );
  }

  render() {
    const { renderModals, isOpen, isAgreed, isDeleted, isLoading } = this.state;
    const { firstName, lastName, type } = this.props;
    const currentText = textContent[type];

    return (
      <div>
        <button
          type="button"
          className="delete supplementary cr-ml-0"
          onClick={() => this.handleOpenModal()}
        >
          {currentText.openModalButton}
        </button>
        {renderModals && (
          <Fragment>
            <Modal isOpen={isOpen} onRequestClose={() => this.handleCloseModal()}>
              <ModalHeader onCloseClicked={() => this.handleCloseModal()}>
                <Text bodyLarge>{currentText.deleteModal.headerTitle}</Text>
              </ModalHeader>
              <ModalBody scroll>
                <div
                  style={{ width: '420px' }}
                  className="cirrus cr-flex cr-width-full cr-justify-between cr-flex-col"
                >
                  <div className="cr-flex cr-width-full cr-justify-between cr-flex-col cr-ph-3 cr-pb-3">
                    <div className="cr-text-center cr-mb-3">
                      <img
                        src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/settings/delete-data.svg"
                        width="94"
                        height="124"
                        alt={currentText.deleteModal.imageTitle}
                      />
                    </div>
                    <div className="cr-mb-3">
                      <Text bodySmall>{currentText.deleteModal.checklistTitle}</Text>
                    </div>
                    <div className="cr-mb-3 cr-radius-1 cr-bg-snow-200">
                      <CardSection large>
                        <Text element="div" bodySmall>
                          <ul className="cr-list-m-0">
                            <li>{sanitizeText(currentText.deleteModal.checklistOptionOne)}</li>
                            <li>{sanitizeText(currentText.deleteModal.checklistOptionTwo)}</li>
                            <li>{sanitizeText(currentText.deleteModal.checklistOptionThree)}</li>
                          </ul>
                        </Text>
                      </CardSection>
                    </div>
                    <div className="cr-mb-3">
                      <Text bodySmall>{currentText.deleteModal.warning}</Text>
                    </div>
                    <div>
                      <Text bodySmall bold>
                        {currentText.deleteModal.confirmation}
                      </Text>
                    </div>
                  </div>
                  <div className="cr-radius-1 cr-border-gray-200 cr-border-style-solid cr-border-width-1">
                    <CardSection>
                      <div className="cr-mh-2 cr-mb-3 cr-mt-1">
                        <Checkbox
                          className="delete-agree"
                          label={sanitizeText(
                            currentText.deleteModal.agreement(firstName, lastName)
                          )}
                          checked={isAgreed}
                          onChange={() => this.handleCheckAgree()}
                        />
                      </div>
                      <Button
                        variant="danger"
                        fill
                        block
                        disabled={!isAgreed || isLoading}
                        loading={isLoading}
                        onClick={() => this.handleClickDelete()}
                      >
                        {currentText.deleteModal.buttonAgree}
                      </Button>
                    </CardSection>
                  </div>
                </div>
              </ModalBody>
            </Modal>
            <Modal isOpen={isDeleted} onRequestClose={() => this.handleReturnToParent()}>
              <ModalHeader onCloseClicked={() => this.handleReturnToParent()}>
                <Text bodyLarge>{currentText.successModal.headerTitle}</Text>
              </ModalHeader>
              <ModalBody>
                <div
                  style={{ width: '420px' }}
                  className="cirrus cr-flex cr-width-full cr-justify-between cr-flex-col"
                >
                  <Button block onClick={() => this.handleReturnToParent()}>
                    {currentText.successModal.returnToListButton}
                  </Button>
                </div>
              </ModalBody>
            </Modal>
          </Fragment>
        )}
      </div>
    );
  }
}

DeleteAccount.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['employee', 'customer']).isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
};

export default DeleteAccount;

const RenderedComponent = createReactRenderer({
  component: DeleteAccount,
  selector: '[data-react-root="DeleteAccount"]',
});

history.onChangeState(
  { name: ['admin.views.employee', 'customer.views.customer'], id: undefined },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
